import {
  MachineTimesOptimizationMetadata,
  Optimization,
  OptimizationTypeSchema,
  ProcessStep,
} from "../types";
import { distributeTimeProRata } from "./distributeTimeProRata";
import { getOptimizationMachineProcessSteps } from "./getOptimizationMachineProcessSteps";

export type OptimizedMachineTime = {
  machinePreparationTime: number;
  otherMachineTime: number;
};

export type OptimizedMachineTimes = ReadonlyMap<string, OptimizedMachineTime>;

export const NO_OPTIMIZED_MACHINE_TIMES: OptimizedMachineTimes = new Map();

export type MachineTimesOptimizationForComputing = Optimization & {
  machineTimesOptimizationMetadata: MachineTimesOptimizationMetadata | null;
  quoteRequestLines: { processSteps: ProcessStep[] }[];
};

const sumTimes = (times: Array<number | null>) =>
  times.reduce<number>((total, time) => total + (time ?? 0), 0);

const distributeTotal = (currentTimes: Array<number | null>, newTotal: number) =>
  newTotal === sumTimes(currentTimes)
    ? currentTimes.map((time) => time ?? 0)
    : distributeTimeProRata({ currentTimes, newTotal });

export const computeOptimizedMachineTimes = (
  optimizations: MachineTimesOptimizationForComputing[],
): OptimizedMachineTimes => {
  const optimizedMachineTimes = new Map<string, OptimizedMachineTime>();

  for (const optimization of optimizations) {
    const metadata = optimization.machineTimesOptimizationMetadata;
    const isAppliedMachineTimesOptimization =
      optimization.enabled && optimization.type === OptimizationTypeSchema.enum.MACHINE_TIMES;
    if (!isAppliedMachineTimesOptimization || !metadata) {
      continue;
    }

    const steps = getOptimizationMachineProcessSteps(optimization).map(({ step }) => step);
    const preparationTimes = distributeTotal(
      steps.map((step) => step.machinePreparationTime),
      metadata.machinePreparationTime,
    );
    const otherMachineTimes = distributeTotal(
      steps.map((step) => step.otherMachineTime),
      metadata.otherMachineTime,
    );

    steps.forEach((step, index) => {
      optimizedMachineTimes.set(step.id, {
        machinePreparationTime: preparationTimes[index] ?? 0,
        otherMachineTime: otherMachineTimes[index] ?? 0,
      });
    });
  }

  return optimizedMachineTimes;
};

export const withOptimizedMachineTimes = <Step extends ProcessStep>(
  processStep: Step,
  optimizedMachineTimes: OptimizedMachineTimes,
): Step => {
  const optimizedTimes = optimizedMachineTimes.get(processStep.id);

  return optimizedTimes ? { ...processStep, ...optimizedTimes } : processStep;
};
